import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import { useParams } from "react-router-dom";

import Template from "../../layout/template";
import {
  getGenericSurvey,
  messageFromError,
  resolveGenericSurveyResponse,
  saveGenericSurveyResponse,
  submitGenericSurveyResponse,
} from "../../utils/api";
import { buildLoginRedirectUrl } from "../../utils/auth-redirect";
import { DEFAULT_PUBLIC_SURVEY_LOGO_URL } from "../../constants/brand";
import {
  createEmbedMessenger,
  observeEmbedHeight,
} from "../../utils/embed-bridge";

const resumeStorageKey = publicKey => `ferasurvey.resume.${publicKey}`;

const readResumeToken = publicKey => {
  try {
    return localStorage.getItem(resumeStorageKey(publicKey)) || "";
  } catch {
    return "";
  }
};

const rememberResumeToken = (publicKey, token) => {
  if (!token) return;
  try {
    localStorage.setItem(resumeStorageKey(publicKey), token);
  } catch {
    // A blocked storage API must not prevent the response from continuing.
  }
};

const freshIdempotencyKey = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = new Uint8Array(24);
  window.crypto?.getRandomValues?.(bytes);
  return Array.from(bytes, value => value.toString(16).padStart(2, "0")).join("");
};

function PublicCard({ children, labelledBy }) {
  return (
    <main className="fera-public-survey-shell">
      <section className="fera-public-survey-card" aria-labelledby={labelledBy}>
        {children}
      </section>
    </main>
  );
}

function GenericSurvey({ embedded = false }) {
  const { publicKey } = useParams();
  const [survey, setSurvey] = useState(null);
  const [authenticationRequired, setAuthenticationRequired] = useState(false);
  const [loginUrl, setLoginUrl] = useState("");
  const [session, setSession] = useState(null);
  const [choices, setChoices] = useState(null);
  const [selectedResponseId, setSelectedResponseId] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [embedContext, setEmbedContext] = useState(null);
  const idempotencyRef = useRef(null);
  const embedRootRef = useRef(null);
  const messengerRef = useRef(null);

  const resumeToken = useMemo(() => readResumeToken(publicKey), [publicKey]);
  const publicLogo = survey?.branding?.logo || DEFAULT_PUBLIC_SURVEY_LOGO_URL;
  const embedRequest = useMemo(() => {
    if (!embedded) return {};
    const query = new URLSearchParams(window.location.search);
    return {
      installation: query.get("installation") || "",
      embed_id: query.get("embed_id") || "",
      correlation_id: query.get("correlation_id") || "",
      return_url: query.get("return_url") || "",
      theme: query.get("theme") || "auto",
      preview_embed: query.get("preview_embed") || "",
    };
  }, [embedded]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getGenericSurvey(publicKey, embedRequest)
      .then(result => {
        if (!active) return;
        if (embedded && !result.embedContext) {
          throw new Error("This WordPress installation is not authorized.");
        }
        setSurvey(result.item);
        setEmbedContext(result.embedContext || null);
        setAuthenticationRequired(Boolean(result.authenticationRequired));
        setLoginUrl(result.loginUrl || "");
        setError("");
      })
      .catch(requestError => {
        if (active) {
          setError(messageFromError(requestError, "This survey could not be opened."));
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [embedRequest, embedded, publicKey]);

  useEffect(() => {
    if (!embedded) return undefined;
    document.body.classList.add("fera-embedded-document");
    return () => document.body.classList.remove("fera-embedded-document");
  }, [embedded]);

  useEffect(() => {
    if (!embedded || !embedContext || loading || !embedRootRef.current) {
      return undefined;
    }
    const messenger = createEmbedMessenger({
      embedId: embedContext.embedId,
      correlationId: embedContext.correlationId,
      parentOrigin: embedContext.parentOrigin,
    });
    messengerRef.current = messenger;
    messenger.send("ferasurvey:ready");
    if (authenticationRequired) {
      messenger.send("ferasurvey:authentication-required");
    }
    const stopObserving = observeEmbedHeight(embedRootRef.current, messenger);
    return () => {
      stopObserving();
      messengerRef.current = null;
    };
  }, [authenticationRequired, embedContext, embedded, loading]);

  const embeddedFrame = useCallback(content => {
    if (!embedded) return content;
    return (
      <div
        ref={embedRootRef}
        className="fera-embed-root"
        data-embed-id={embedContext?.embedId || ""}
        data-theme={embedContext?.theme || "auto"}
      >
        {content}
      </div>
    );
  }, [embedContext, embedded]);

  const begin = useCallback(async (values = {}) => {
    if (embedContext?.preview) return;
    setResolving(true);
    setError("");
    try {
      const result = await resolveGenericSurveyResponse(
        publicKey,
        values,
        readResumeToken(publicKey)
      );
      if (result.requiresChoice) {
        setChoices(result);
        setSelectedResponseId(result.responses?.[0]?.id || "");
        return;
      }
      if (result.resumeToken) {
        rememberResumeToken(publicKey, result.resumeToken);
      }
      setChoices(null);
      setSession({
        response: result.response,
        version: Number(result.version || 0),
        data: result.data || {},
      });
      messengerRef.current?.send("ferasurvey:started");
    } catch (requestError) {
      setError(messageFromError(requestError, "Your response could not be opened."));
    } finally {
      setResolving(false);
    }
  }, [embedContext?.preview, publicKey]);

  const persist = useCallback(async ({ data, submit }) => {
    if (!session?.response?.id) return { success: false };
    const action = submit ? "submit" : "save";
    const requestFingerprint = `${action}:${session.version}:${JSON.stringify(data)}`;
    if (idempotencyRef.current?.fingerprint !== requestFingerprint) {
      idempotencyRef.current = {
        fingerprint: requestFingerprint,
        key: freshIdempotencyKey(),
      };
    }
    setError("");
    try {
      const request = submit ? submitGenericSurveyResponse : saveGenericSurveyResponse;
      const result = await request(
        publicKey,
        session.response.id,
        { data, version: session.version },
        readResumeToken(publicKey),
        idempotencyRef.current.key
      );
      setSession(current => ({
        ...current,
        response: result.response || current.response,
        version: Number(result.version),
        data,
      }));
      idempotencyRef.current = null;
      if (submit) {
        setCompleted(true);
        messengerRef.current?.send("ferasurvey:submitted");
      } else {
        messengerRef.current?.send("ferasurvey:saved");
      }
      return { success: true };
    } catch (requestError) {
      const details = requestError?.response?.data?.error?.details;
      if (details?.draftSaved && Number.isFinite(Number(details.responseVersion))) {
        setSession(current => ({
          ...current,
          version: Number(details.responseVersion),
          data,
        }));
      } else if (
        requestError?.response?.data?.error?.code === "response_version_conflict"
        && Number.isFinite(Number(details?.currentVersion))
      ) {
        setSession(current => ({
          ...current,
          version: Number(details.currentVersion),
          data: details.data || current.data,
        }));
      }
      setError(messageFromError(
        requestError,
        submit
          ? "Your answers were saved, but the response could not be submitted."
          : "Your progress could not be saved. Your answers are still on this page."
      ));
      messengerRef.current?.send("ferasurvey:error");
      return { success: false };
    }
  }, [publicKey, session]);

  if (loading) {
    return embeddedFrame(
      <PublicCard labelledBy="survey-loading-title">
        <div className="fera-public-loading" role="status">
          <Spinner animation="border" size="sm" />
          <span id="survey-loading-title">Opening secure survey&hellip;</span>
        </div>
      </PublicCard>
    );
  }

  if (!survey) {
    return embeddedFrame(
      <PublicCard labelledBy="survey-error-title">
        <p className="fera-eyebrow">FeraSurvey</p>
        <h1 id="survey-error-title">Survey unavailable</h1>
        <Alert variant="danger">{error}</Alert>
      </PublicCard>
    );
  }

  if (authenticationRequired) {
    let target;
    if (embedded) {
      const secureSurvey = new URL(
        `/s/${encodeURIComponent(publicKey)}`,
        window.location.origin
      );
      secureSurvey.searchParams.set(
        "installation",
        embedContext?.installationId || ""
      );
      if (embedContext?.returnUrl) {
        secureSurvey.searchParams.set("return_url", embedContext.returnUrl);
      }
      target = secureSurvey.toString();
    } else {
      target = buildLoginRedirectUrl(loginUrl, window.location.href) || loginUrl;
    }
    return embeddedFrame(
      <PublicCard labelledBy="survey-auth-title">
        <span className="fera-product-mark" aria-hidden="true">SV</span>
        <p className="fera-eyebrow">FeraSurvey</p>
        <h1 id="survey-auth-title">{survey.title}</h1>
        <p className="text-muted">Sign in with your Feradel account to answer this survey.</p>
        <a
          className="btn btn-primary w-100"
          href={target}
          target={embedded ? "_blank" : undefined}
          rel={embedded ? "noopener noreferrer" : undefined}
        >
          {embedded ? "Open secure survey" : "Continue to Feradel Auth"}
        </a>
      </PublicCard>
    );
  }

  if (completed) {
    return embeddedFrame(
      <PublicCard labelledBy="survey-complete-title">
        <span className="fera-product-mark fera-product-mark-success" aria-hidden="true">{"\u2713"}</span>
        <p className="fera-eyebrow">Response received</p>
        <h1 id="survey-complete-title">Thank you</h1>
        <p className="text-muted mb-0">Your response to {survey.title} was submitted successfully.</p>
        {survey.allowSubmittedResponseUpdates ? (
          <Button
            type="button"
            variant="outline-primary"
            className="w-100 mt-4"
            onClick={() => setCompleted(false)}
          >
            Change my response
          </Button>
        ) : null}
      </PublicCard>
    );
  }

  if (choices) {
    return embeddedFrame(
      <PublicCard labelledBy="survey-choice-title">
        <p className="fera-eyebrow">Previous response found</p>
        <h1 id="survey-choice-title">Continue {survey.title}</h1>
        <p className="text-muted">Update Existing is selected to protect your prior work and avoid an accidental duplicate.</p>
        {error ? <Alert variant="danger">{error}</Alert> : null}
        <Form onSubmit={event => {
          event.preventDefault();
          begin({ choice: "update", responseId: selectedResponseId });
        }}>
          <fieldset>
            <legend className="h6">Choose a response</legend>
            {choices.responses.map(item => (
              <Form.Check
                key={item.id}
                type="radio"
                id={`response-${item.id}`}
                name="existing-response"
                value={item.id}
                checked={selectedResponseId === item.id}
                onChange={event => setSelectedResponseId(event.target.value)}
                label={`${item.status === "submitted" ? "Submitted" : "Draft"} \u00b7 Updated ${new Date(item.updatedAt).toLocaleString()}`}
                className="fera-response-choice"
              />
            ))}
          </fieldset>
          <div className="d-grid gap-2 mt-4">
            <Button type="submit" disabled={resolving} autoFocus>
              {resolving ? "Opening\u2026" : "Update Existing"}
            </Button>
            {choices.allowNew ? (
              <Button
                type="button"
                variant="outline-secondary"
                disabled={resolving}
                onClick={() => begin({ choice: "new" })}
              >
                Start New Response
              </Button>
            ) : null}
          </div>
        </Form>
      </PublicCard>
    );
  }

  if (!session) {
    return embeddedFrame(
      <PublicCard labelledBy="survey-start-title">
        <img
          className="fera-public-logo"
          src={publicLogo}
          alt={publicLogo === DEFAULT_PUBLIC_SURVEY_LOGO_URL
            ? "Feradel"
            : survey.branding?.name || "Survey logo"}
        />
        <p className="fera-eyebrow">{survey.branding?.name || "FeraSurvey"}</p>
        <h1 id="survey-start-title">{survey.title}</h1>
        {survey.description ? <p className="text-muted">{survey.description}</p> : null}
        {error ? <Alert variant="danger">{error}</Alert> : null}
        {embedContext?.preview ? (
          <Alert variant="info" className="mb-0">
            Editor preview only. Starting and saving responses is disabled.
          </Alert>
        ) : (
          <Button className="w-100" disabled={resolving} onClick={() => begin()}>
            {resolving ? "Opening\u2026" : resumeToken ? "Continue survey" : "Start survey"}
          </Button>
        )}
      </PublicCard>
    );
  }

  return embeddedFrame(
    <main className="fera-public-response">
      {error ? (
        <Alert variant="danger" className="fera-public-response-error" role="alert">
          {error}
        </Alert>
      ) : null}
      <Template
        key={session.response.id}
        name={survey.title}
        description={survey.description}
        Sections={survey.sections}
        data={session.data}
        private
        skipPrivacyPrompt
        allowDynamicDisplay={false}
        autosave
        autosaveIntervalMs={1500}
        invite={session.response.id}
        logo={publicLogo}
        showSectionBrowser={survey.showSectionBrowser}
        onPersist={persist}
        richTextLabels
      />
    </main>
  );
}

export {
  freshIdempotencyKey,
  readResumeToken,
  rememberResumeToken,
};

export default GenericSurvey;
